/* eslint-disable */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { leaderApi } from '../../../api/leaderApi';
import WorkerSidebar from '../../../components/tools/leader/WorkerSidebar';
import CalendarView from '../../../components/tools/leader/CalendarView/CalendarView';
import ListView from '../../../components/tools/leader/ListView';
import UndatierteSidebar from '../../../components/tools/leader/UndatierteSidebar';

import { 
    toLocalISO, 
    getAssignmentConflicts
} from '../../../utils/leaderUtils';
import { useLeaderPermissions } from '../../../hooks/useLeaderPermissions';
import { useLeaderData } from '../../../hooks/useLeaderData';
import { useLeaderNavigation } from './hooks/useLeaderNavigation';
import { usePersistentListViewState } from './hooks/usePersistentListViewState';
import { useLeaderHistory } from './hooks/useLeaderHistory';
import { useLeaderConflicts } from './hooks/useLeaderConflicts';
import { useLeaderInteractions } from './hooks/useLeaderInteractions';
import { useLeaderAssignments } from './hooks/useLeaderAssignments';
import { useLeaderResources } from './hooks/useLeaderResources';
import { useLeaderModals } from './hooks/useLeaderModals';
import { useLeaderPageStateBackup } from './hooks/useLeaderPageStateBackup';
import LeaderModalsOrchestrator from '../../../components/tools/leader/LeaderModalsOrchestrator';
import LeaderEditor from '../../../components/tools/leader/LeaderEditor';
import '../../../components/tools/leader/Leader.css';
import '../../../components/tools/leader/ContextMenu.css';
import '../../../components/tools/leader/SharedModals.css';
import '../../../components/tools/leader/UndatierteSidebar.css';
import '../../../components/tools/leader/AssignmentPanel/AssignmentPanel.css';
import '../../../components/tools/leader/ContextCalendar.css';

import '../../../components/tools/leader/SharedComponents.css';


const STATUS_OPTIONS = [
    { value: 'aktiv', label: 'Aktiv', color: '#2563eb' },
    { value: 'pausiert', label: 'Pausiert', color: '#ca8a04' },
    { value: 'abgeschlossen', label: 'Abgeschlossen', color: '#16a34a' }
];

/**
 * Main orchestrator for the Leader (Disponent) tool.
 * Handles view switching (Calendar vs List), global resource management, 
 * and assignment creation/editing.
 * 
 * Supports Rank 2 (Info-Ansicht) for read-only personal data access.
 * 
 * @component
 */
export default function LeaderPage() {
    const [viewMode, setViewMode] = useState('days'); // 'month' or 'days'
    const navigate = useNavigate();
    const navigation = useLeaderNavigation(setViewMode);
    const listViewPersistence = usePersistentListViewState();
    const location = useLocation();
    const { logout, user } = useAuth();
    const { isReadOnly } = useLeaderPermissions();

    // Data-Management via Hook
    const {
        assignments, setAssignments,
        absences, setAbsences,
        assignmentsForConflicts, setAssignmentsForConflicts,
        absencesForConflicts, setAbsencesForConflicts,
        allAssignments, setAllAssignments,
        workers, setWorkers,
        vehicles, setVehicles,
        kolonnen,
        loading,
        listLoading, setListLoading,  // Need listLoading here
        loadData,
        loadAllAssignments,
        loadHistoryData,
        categories,
        companyLoc
    } = useLeaderData({ 
        isReadOnly, 
        workerId: user?.worker_id 
    });

    // Undo/Redo History & Sorting via Hook
    const {
        assignmentHistory,
        redoHistory,
        sortAssignmentsByVisualOrder,
        pushToHistory,
        handleUndo,
        handleRedo
    } = useLeaderHistory({ assignments, setAssignments });

    // Main view mode: 'calendar' or 'list'
    const [mainViewMode, setMainViewMode] = useState('calendar'); // 'calendar' or 'list'
    const [viewDays, setViewDays] = useState(3);
    const [sidebarMode, setSidebarMode] = useState(() => localStorage.getItem('leaderSidebarMode') || 'resources');

    useEffect(() => {
        localStorage.setItem('leaderSidebarMode', sidebarMode);
    }, [sidebarMode]);

    // List view: all assignments (no date range)
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    });

    // --- UI State & Navigation Hooks ---
    const [exportSelectedIds, setExportSelectedIds] = useState([]);
    const [exportInitialFilters, setExportInitialFilters] = useState(null);

    const {
        exportOpen, setExportOpen,
        conflictOverviewOpen, setConflictOverviewOpen,
        absenceModalOpen, setAbsenceModalOpen,
        projectModalOpen, setProjectModalOpen,
        kolonnenManagementOpen, setKolonnenManagementOpen,
        vehicleModalOpen, setVehicleModalOpen,
        clientModalOpen, setClientModalOpen,
        workerModalOpen, setWorkerModalOpen,
        editingKolonneId, setEditingKolonneId,
        absenceWorkerId, setAbsenceWorkerId,
        focusResource, setFocusResource,
        editingProjectId, setEditingProjectId,
        closeConflictOverview,
        openKolonnenManagement
    } = useLeaderModals();

    const {
        editStack, setEditStack,
        forwardStack, setForwardStack,
        editingAssignment, setEditingAssignment,
        modalOpen, setModalOpen,
        presetDate, setPresetDate,
        isUndatedPreset, setIsUndatedPreset,
        navigationSource, setNavigationSource,
        activeResources, setActiveResources,
        activeDateRange, setActiveDateRange,
        handleAssignmentClick,
        handleNavigateBack,
        handleNavigateForward,
        handleExportShortcut,
        handleOpenExport,
        handleNewAssignment,
        handleUndatedClick,
        resetNavigation
    } = useLeaderNavigation({
        allAssignments,
        loadAllAssignments,
        setExportSelectedIds,
        setExportInitialFilters,
        setExportOpen
    });

    // Restore and Persist Leader Page State
    useLeaderPageStateBackup(
        {
            mainViewMode,
            startDate,
            viewMode,
            viewDays,
            sidebarMode,
            modalOpen,
            editingAssignment,
            presetDate,
            isUndatedPreset,
            activeResources,
            activeDateRange
        },
        {
            setMainViewMode,
            setStartDate,
            setViewMode,
            setViewDays,
            setSidebarMode,
            setModalOpen,
            setEditingAssignment,
            setPresetDate,
            setIsUndatedPreset,
            setActiveResources,
            setActiveDateRange
        }
    );

    // --- Helpers (Stable via useCallback) ---
    const getDateRange = useCallback(() => {
        if (viewMode === 'month') {
            const start = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
            start.setDate(start.getDate() - 7);
            const end = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);
            end.setDate(end.getDate() + 7);
            return {
                start: start.toISOString().split('T')[0],
                end: end.toISOString().split('T')[0]
            };
        }
        const start = new Date(startDate);
        start.setDate(start.getDate() - 1);
        const end = new Date(startDate);
        end.setDate(end.getDate() + viewDays + 1);
        return {
            start: start.toISOString().split('T')[0],
            end: end.toISOString().split('T')[0]
        };
    }, [startDate, viewDays, viewMode]);

    const loadDateDependentData = useCallback(() => loadData(getDateRange()), [loadData, getDateRange]);
    const loadGlobalConflictData = useCallback(() => loadData(getDateRange()), [loadData, getDateRange]);

    const leaderViewportRef = React.useRef(null);
    const panelRef = React.useRef(null);
    const [undatierteSidebarOpen, setUndatierteSidebarOpen] = useState(false);
    const [interaction, setInteraction] = useState(null);

    // Conflict Management via Hook
    const {
        calculatedConflicts,
        logicalConflictCount,
        isConflictDetectionEnabled,
        handleToggleConflictDetection,
        conflictActiveTab, setConflictActiveTab,
        conflictExpandedResources, setConflictExpandedResources,
        conflictCollapsedSections, setConflictCollapsedSections
    } = useLeaderConflicts({
        workers,
        vehicles,
        assignmentsForConflicts,
        absencesForConflicts,
        kolonnen
    });

    // Assignment CRUD Hook
    const {
        handleSaveAssignment,
        handleDeleteAssignment,
        handleMoveToUndated,
        syncConflictData
    } = useLeaderAssignments({
        setAssignments,
        setAllAssignments,
        setAssignmentsForConflicts,
        setEditStack,
        setForwardStack,
        setEditingAssignment,
        setModalOpen,
        setActiveResources,
        setInteraction,
        setConflictOverviewOpen,
        setNavigationSource,
        assignmentsForConflicts,
        absencesForConflicts,
        workers,
        vehicles,
        kolonnen,
        isConflictDetectionEnabled,
        editStack,
        mainViewMode,
        navigationSource,
        modalOpen,
        pushToHistory,
        resetNavigation,
        loadData,
        loadAllAssignments,
        getDateRange
    });

    // Interaction management initialized before usages
    const {
        externalDraggingAssignment, setExternalDraggingAssignment,
        hoveredAssignment,
        hoverCardPos,
        hoveredAssignmentConflicts,
        hoveredConflict,
        hoveredConflictPos,
        contextMenu, setContextMenu,
        clipboard, setClipboard,
        handleAssignmentHover,
        handleExportAssignmentHover,
        clearAssignmentHover,
        handleConflictHover,
        clearConflictHover,
        handleContextMenu,
        handleMenuAction,
        onHoverCardEnter,
        onConflictHoverCardEnter,
        getPopupPosition
    } = useLeaderInteractions({
        assignmentsForConflicts,
        absencesForConflicts,
        vehicles,
        isConflictDetectionEnabled,
        onAssignmentClick: handleAssignmentClick,
        onAssignmentDelete: (id) => handleDeleteAssignment(id),
        onAssignmentSave: (data) => handleSaveAssignment(data),
        onMoveToUndated: (assignment) => handleMoveToUndated(assignment),
        toLocalISO,
        interaction,
        setInteraction
    });

    // Resource Management Hook
    const {
        handleOpenWorkerModal,
        handleOpenKolonnenManagement,
        handleOpenConflictOverview
    } = useLeaderResources({
        setEditingKolonneId,
        setKolonnenManagementOpen,
        setFocusResource,
        setConflictOverviewOpen,
        conflictExpandedResources,
        conflictCollapsedSections,
        navigate
    });


    useEffect(() => {
        return () => {};
    }, []);

    useEffect(() => {
        if (interaction) {
            clearAssignmentHover();
            clearConflictHover();
        }
    }, [interaction]);

    const getAssignmentPopupPosition = getPopupPosition;
    const getConflictPopupPosition = (rect, cw, ch, fp, opts) => getPopupPosition(rect, cw, ch, fp, { ...opts, isConflict: true });



    // Data fetching is now mostly managed by useLeaderData hook.
    useEffect(() => {
        loadData(getDateRange());
        loadHistoryData(); // Load background history for conflicts
    }, [loadData, loadHistoryData, getDateRange]);


    // Effect to handle restoration from navigation state
    useEffect(() => {
        if (location.state?.restoreConflict) {
            const savedState = location.state.conflictState;
            if (savedState) {
                if (savedState.activeTab) {
                    setConflictActiveTab(savedState.activeTab);
                }
                if (savedState.expandedResources) {
                    setConflictExpandedResources(new Set(savedState.expandedResources));
                }
                if (savedState.collapsedSections) {
                    setConflictCollapsedSections(new Set(savedState.collapsedSections));
                }
                setConflictOverviewOpen(true);
            }
        }
        
        // SYNC: Refresh data when returning from employee management to catch color changes
        if (location.state?.from === '/tools/employee') {
            loadData(getDateRange());
        }
    }, [location.state, loadData, getDateRange]);

    useEffect(() => {
        if (!loading) { 
            loadData(getDateRange());
        }
    }, [startDate, viewDays, viewMode, loadData, getDateRange]);

    // loadAllAssignments logic moved to hook.

    useEffect(() => {
        if (mainViewMode === 'list') {
            loadAllAssignments();
        }
    }, [mainViewMode, loadAllAssignments]);

    useEffect(() => {
        const assignmentId = location.state?.openAssignmentId || new URLSearchParams(location.search).get('assignmentId');
        if (assignmentId) {
            loadAllAssignments();
        }
    }, [location.state, location.search, loadAllAssignments]);

    useEffect(() => {
        const assignmentId = location.state?.openAssignmentId || new URLSearchParams(location.search).get('assignmentId');
        if (assignmentId && allAssignments.length > 0) {
            const assignment = allAssignments.find(a => String(a.id) === String(assignmentId)) ||
                               assignments.find(a => String(a.id) === String(assignmentId));
            if (assignment) {
                handleAssignmentClick(assignment);
                // Clear state
                if (location.state?.openAssignmentId) {
                    navigate(location.pathname, { replace: true, state: { ...location.state, openAssignmentId: undefined } });
                }
            }
        }
    }, [location.state, location.search, allAssignments, assignments, handleAssignmentClick, navigate]);

    const handleSwitchToList = () => {
        setMainViewMode('list');
    };

    const handleSwitchToCalendar = () => {
        setMainViewMode('calendar');
        loadDateDependentData();
    };

    // Worker and Vehicle actions are now handled by useLeaderResources hook.

    // --- Calendar Navigation ---
    const handleNavigate = (direction) => {
        if (direction === 'today') {
            const d = new Date();
            d.setHours(0, 0, 0, 0);
            setStartDate(d);
        } else {
            setStartDate(prev => {
                const d = new Date(prev);
                d.setDate(d.getDate() + direction);
                return d;
            });
        }
    };

    const handleViewChange = (days) => setViewDays(days);
    const handleViewModeChange = (mode) => setViewMode(mode);

    const handleSetStartDate = (date) => {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        setStartDate(d);
    };

    /**
     * Schließt den Editor und setzt den Navigationsverlauf zurück.
     */
    const handleCloseEditor = () => {
        resetNavigation();
        if (navigationSource === 'conflict-overview') {
            setConflictOverviewOpen(true);
        }
        setNavigationSource(null);
    };

    const handleEditAssignmentFromConflict = (param) => {
        let assignment = null;
        if (typeof param === 'object' && param !== null) {
            assignment = param;
        } else {
            const id = String(param);
            assignment = assignments.find(a => String(a.id) === id) || 
                         allAssignments.find(a => String(a.id) === id) ||
                         assignmentsForConflicts.find(a => String(a.id) === id);
        }

        if (assignment) {
            setNavigationSource('conflict-overview');
            setEditingAssignment(assignment);
            setModalOpen(true);
            setConflictOverviewOpen(false);
        }
    }


    async function handleDropWorker(workerData, day, targetAssignment = null, isUndated = false) {
        // --- CASE A: Drop on existing assignment (Silent Add if new) ---
        if (targetAssignment) {
            let isNew = false;
            let updatedAssignment = { ...targetAssignment };
            updatedAssignment.workers = [...(targetAssignment.workers || [])];
            updatedAssignment.vehicles = [...(targetAssignment.vehicles || [])];

            if (workerData.type === 'worker') {
                const alreadyExists = updatedAssignment.workers.some(w => String(w.worker_id) === String(workerData.id));
                if (!alreadyExists) {
                    isNew = true;
                    updatedAssignment.workers.push({ 
                        worker_id: workerData.id, 
                        name: workerData.name, 
                        assignment_role: workerData.role || 'Monteur',
                        kolonne_id: null 
                    });
                }
            } else if (workerData.type === 'vehicle') {
                const alreadyExists = updatedAssignment.vehicles.some(v => String(v.vehicle_id) === String(workerData.id));
                if (!alreadyExists) {
                    isNew = true;
                    updatedAssignment.vehicles.push({ 
                        vehicle_id: workerData.id, 
                        kolonne_id: null 
                    });
                }
            } else if (workerData.type === 'kolonne') {
                const kolonne = kolonnen.find(k => String(k.id) === String(workerData.id));
                if (kolonne && kolonne.members) {
                    kolonne.members.forEach(m => {
                        if (m.type === 'worker') {
                            const exists = updatedAssignment.workers.some(w => String(w.worker_id) === String(m.resource_id));
                            if (!exists) {
                                isNew = true;
                                const master = workers.find(w => w.id === m.resource_id);
                                updatedAssignment.workers.push({ 
                                    worker_id: m.resource_id, 
                                    name: master?.name || '', 
                                    assignment_role: master?.role || 'Monteur',
                                    kolonne_id: kolonne.id 
                                });
                            }
                        } else if (m.type === 'vehicle') {
                            const exists = updatedAssignment.vehicles.some(v => String(v.vehicle_id) === String(m.resource_id));
                            if (!exists) {
                                isNew = true;
                                updatedAssignment.vehicles.push({ 
                                    vehicle_id: m.resource_id, 
                                    kolonne_id: kolonne.id 
                                });
                            }
                        }
                    });
                }
            }

            if (isNew) {
                // Silent Update
                await handleSaveAssignment(updatedAssignment);
                return; // Modal remains closed
            }

            // If not new (already exists), fall through to open modal
            setEditingAssignment(targetAssignment);
            setPresetDate(null);
            setIsUndatedPreset(false);
        } 
        // --- CASE B: Drop on empty space (Create new with Modal) ---
        else {
            setEditingAssignment(null);
            setPresetDate(day);
            setIsUndatedPreset(isUndated);
        }

        setModalOpen(true);
        // Delay resource assignment so AssignmentPanel has time to mount and register
        setTimeout(() => {
            if (workerData.type === 'worker') {
                panelRef.current?.addResource({ 
                    type: 'worker', 
                    id: workerData.id, 
                    name: workerData.name, 
                    role: workerData.role 
                });
            } else if (workerData.type === 'vehicle') {
                panelRef.current?.addResource({ 
                    type: 'vehicle', 
                    id: workerData.id, 
                    license_plate: workerData.license_plate, 
                    internal_name: workerData.internal_name 
                });
            } else if (workerData.type === 'kolonne') {
                // Bulk add kolonne members
                const kolonne = kolonnen.find(k => String(k.id) === String(workerData.id));
                if (kolonne && kolonne.members) {
                    kolonne.members.forEach(m => {
                        const res = m.type === 'worker' ? workers.find(w => w.id === m.resource_id) : vehicles.find(v => v.id === m.resource_id);
                        panelRef.current?.addResource({ 
                            type: m.type, 
                            id: m.resource_id, 
                            kolonne_id: kolonne.id,
                            name: res?.name || res?.license_plate || res?.internal_name || '',
                            role: res?.role || ''
                        });
                    });
                }
            }
        }, 50);
    };

    // handleSaveAssignment, handleDeleteAssignment and handleMoveToUndated are now provided by useLeaderAssignments hook.



    // handleOpenConflictOverview is now handled by useLeaderResources hook.





    if (loading) {
        return (
            <div className="leader-container" style={{ alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <div className="empty-state">
                    <div className="empty-state-icon">⏳</div>
                    <div className="empty-state-text">Kolonnenführer lädt...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="leader-app-wrapper" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

            {/* ===== HAUPTINHALT (Dynamisches Layout) ===== */}
                <div ref={leaderViewportRef} className={`leader-container ${modalOpen ? 'edit-mode' : ''} ${isReadOnly ? 'read-only-mode' : ''}`}>
                {/* Linke Sidebar: Mitarbeiter + Fahrzeuge (Nur wenn NICHT Read-Only) */}
                {!isReadOnly && (
                    <WorkerSidebar
                        workers={workers}
                        vehicles={vehicles}
                        categories={categories}
                        kolonnen={kolonnen}
                        sidebarMode={sidebarMode}
                        onModeChange={setSidebarMode}
                        assignmentsForConflicts={assignmentsForConflicts}
                        absences={absencesForConflicts}
                        absencesForConflicts={absencesForConflicts}
                        calculatedConflicts={calculatedConflicts}
                        logicalConflictCount={logicalConflictCount}
                        isConflictDetectionEnabled={isConflictDetectionEnabled}
                        onOpenWorkerModal={handleOpenWorkerModal}
                        onOpenKolonnenManagement={handleOpenKolonnenManagement}
                        onOpenConflictOverview={handleOpenConflictOverview}
                        onConflictHover={handleConflictHover}
                        onConflictHoverEnd={clearConflictHover}
                        isEditMode={modalOpen}
                        onAddResource={(res) => panelRef.current?.addResource(res)}
                        onRemoveResource={(res) => panelRef.current?.removeResource(res)}
                        assignedWorkers={activeResources.workers || []}
                        assignedVehicles={activeResources.vehicles || []}
                        interaction={interaction}
                        setInteraction={setInteraction}
                    />
                )}

                {/* --- NORMAL MODUS (Kalender oder Liste) --- */}
                {!modalOpen && (
                    mainViewMode === 'list' ? (
                    <ListView
                        assignments={allAssignments}
                        workers={workers}
                        vehicles={vehicles}
                        kolonnen={kolonnen}
                        assignmentsForConflicts={assignmentsForConflicts}
                        absencesForConflicts={absencesForConflicts}
                        isConflictDetectionEnabled={isConflictDetectionEnabled}
                        persistedState={listViewPersistence}
                        onAssignmentClick={handleAssignmentClick}
                        onAssignmentHover={handleAssignmentHover}
                        onAssignmentHoverEnd={clearAssignmentHover}
                        onAssignmentDelete={handleDeleteAssignment}
                        onNewAssignment={handleNewAssignment}
                        onSwitchToCalendar={() => setMainViewMode('calendar')}
                        onExport={handleOpenExport}
                        onEditResource={(id, type) => {
                            const conflictState = {
                                expandedResources: Array.from(conflictExpandedResources),
                                collapsedSections: Array.from(conflictCollapsedSections)
                            };
                            if (type === 'worker') {
                                navigate('/verwaltung/mitarbeiter', { state: { from: '/tools/leader', openWorkerId: id, conflictState } });
                            } else if (type === 'vehicle') {
                                navigate('/verwaltung/fuhrpark', { state: { fromLeader: true, conflictState } });
                            }
                        }}
                    />
                ) : (
                    <CalendarView
                        startDate={startDate}
                        assignments={assignments}
                        workers={workers}
                        vehicles={vehicles}
                        viewMode={viewMode}
                        viewDays={viewDays}
                        onDayClick={(day) => {
                            if (viewMode === 'month') {
                                setViewMode('days');
                                setStartDate(day);
                                setViewDays(1);
                            } else {
                                setEditingAssignment(null);
                                setPresetDate(day);
                                setIsUndatedPreset(false);
                                setModalOpen(true);
                            }
                        }}
                        onAssignmentClick={handleAssignmentClick}
                        onDropWorker={handleDropWorker}
                        onNavigate={handleNavigate}
                        onViewModeChange={setViewMode}
                        onViewChange={setViewDays}
                        onSetStartDate={setStartDate}
                        onAssignmentHover={handleAssignmentHover}
                        onAssignmentHoverEnd={clearAssignmentHover}
                        onConflictHover={handleConflictHover}
                        onConflictHoverEnd={clearConflictHover}
                        onNewAssignment={handleNewAssignment}
                        onAssignmentUpdate={handleSaveAssignment}
                        onAssignmentDelete={handleDeleteAssignment}
                        onUndo={handleUndo}
                        onRedo={handleRedo}
                        canUndo={assignmentHistory.length > 0}
                        canRedo={redoHistory.length > 0}
                        onSwitchToList={() => setMainViewMode('list')}
                        onExport={handleOpenExport}
                        onMoveToUndated={handleMoveToUndated}
                        isSidebarOpen={undatierteSidebarOpen}
                        isConflictDetectionEnabled={isConflictDetectionEnabled}
                        externalDraggingAssignment={externalDraggingAssignment}
                        interaction={interaction}
                        setInteraction={setInteraction}
                        onContextMenu={handleContextMenu}
                        clipboard={clipboard}
                        absences={absences}
                        assignmentsForConflicts={assignmentsForConflicts}
                        absencesForConflicts={absencesForConflicts}
                        kolonnen={kolonnen}
                        isReadOnly={isReadOnly}
                    />
                ))}
                
                {/* --- EDITOR MODAL (Inline) --- */}
                <LeaderEditor 
                    modalOpen={modalOpen}
                    isReadOnly={isReadOnly}
                    editingAssignment={editingAssignment}
                    editStack={editStack}
                    forwardStack={forwardStack}
                    presetDate={presetDate}
                    isUndatedPreset={isUndatedPreset}
                    activeDateRange={activeDateRange}
                    activeResources={activeResources}
                    workers={workers}
                    vehicles={vehicles}
                    kolonnen={kolonnen}
                    assignments={assignments}
                    absences={absencesForConflicts}
                    assignmentsForConflicts={assignmentsForConflicts}
                    absencesForConflicts={absencesForConflicts}
                    categories={categories}
                    companyLoc={companyLoc}
                    isConflictDetectionEnabled={isConflictDetectionEnabled}
                    panelRef={panelRef}
                    onCloseEditor={handleCloseEditor}
                    onSaveAssignment={handleSaveAssignment}
                    onDeleteAssignment={handleDeleteAssignment}
                    onResourcesChange={setActiveResources}
                    onDateRangeChange={setActiveDateRange}
                    onExportShortcut={handleExportShortcut}
                    onAssignmentClick={handleAssignmentClick}
                    onAssignmentHover={handleAssignmentHover}
                    onAssignmentHoverEnd={clearAssignmentHover}
                    onNavigateBack={handleNavigateBack}
                    onNavigateForward={handleNavigateForward}
                />

                {mainViewMode !== 'list' && !modalOpen && (
                    <UndatierteSidebar 
                        assignments={assignments}
                        onAssignmentClick={handleAssignmentClick}
                        onAssignmentHover={handleAssignmentHover}
                        onAssignmentHoverEnd={clearAssignmentHover}
                        isOpen={undatierteSidebarOpen}
                        onClose={() => setUndatierteSidebarOpen(false)}
                        onToggle={() => setUndatierteSidebarOpen(!undatierteSidebarOpen)}
                        isConflictDetectionEnabled={isConflictDetectionEnabled}
                        onDrop={handleMoveToUndated}
                        onDropWorker={handleDropWorker}
                        onExternalDragStart={setExternalDraggingAssignment}
                        onExternalDragEnd={() => {
                            setExternalDraggingAssignment(null);
                            setInteraction(null);
                        }}
                        interaction={interaction}
                        setInteraction={setInteraction}
                        onContextMenu={handleContextMenu}
                        clipboard={clipboard}
                        onAddUndated={handleUndatedClick}
                        isReadOnly={isReadOnly}
                    />
                )}
                </div>

            {/* --- Modals Orchestrator --- */}
            <LeaderModalsOrchestrator 
                modalOpen={modalOpen}
                isReadOnly={isReadOnly}
                editingAssignment={editingAssignment}
                editStack={editStack}
                forwardStack={forwardStack}
                presetDate={presetDate}
                isUndatedPreset={isUndatedPreset}
                navigationSource={navigationSource}
                exportOpen={exportOpen}
                conflictOverviewOpen={conflictOverviewOpen}
                kolonnenManagementOpen={kolonnenManagementOpen}
                absenceModalOpen={absenceModalOpen}
                user={user}
                allAssignments={allAssignments}
                workers={workers}
                vehicles={vehicles}
                kolonnen={kolonnen}
                assignments={assignments}
                absences={absencesForConflicts}
                assignmentsForConflicts={assignmentsForConflicts}
                absencesForConflicts={absencesForConflicts}
                categories={categories}
                companyLoc={companyLoc}
                activeResources={activeResources}
                activeDateRange={activeDateRange}
                calculatedConflicts={calculatedConflicts}
                logicalConflictCount={logicalConflictCount}
                focusResource={focusResource}
                editingKolonneId={editingKolonneId}
                absenceWorkerId={absenceWorkerId}
                isConflictDetectionEnabled={isConflictDetectionEnabled}
                panelRef={panelRef}
                hoveredAssignment={hoveredAssignment}
                hoveredAssignmentConflicts={hoveredAssignmentConflicts}
                hoverCardPos={hoverCardPos}
                hoveredConflict={hoveredConflict}
                hoveredConflictPos={hoveredConflictPos}
                interaction={interaction}
                exportSelectedIds={exportSelectedIds}
                exportInitialFilters={exportInitialFilters}
                onLoadData={loadData}
                onCloseEditor={handleCloseEditor}
                onSaveAssignment={handleSaveAssignment}
                onDeleteAssignment={handleDeleteAssignment}
                onResourcesChange={setActiveResources}
                onDateRangeChange={setActiveDateRange}
                onExportShortcut={handleExportShortcut}
                onAssignmentClick={handleAssignmentClick}
                onAssignmentHover={handleAssignmentHover}
                onExportAssignmentHover={handleExportAssignmentHover}
                onAssignmentHoverEnd={clearAssignmentHover}
                onConflictHoverEnd={clearConflictHover}
                onHoverCardEnter={onHoverCardEnter}
                onConflictHoverCardEnter={onConflictHoverCardEnter}
                onNavigateBack={handleNavigateBack}
                onNavigateForward={handleNavigateForward}
                onCloseExport={() => setExportOpen(false)}
                onCloseConflictOverview={closeConflictOverview}
                onToggleDetection={handleToggleConflictDetection}
                onEditAssignmentFromConflict={handleEditAssignmentFromConflict}
                onEditResourceFromConflict={(id, type, options = {}) => {
                    setNavigationSource('conflict-overview');
                    const conflictState = {
                        activeTab: conflictActiveTab,
                        expandedResources: Array.from(conflictExpandedResources),
                        collapsedSections: Array.from(conflictCollapsedSections)
                    };
                    if (type === 'worker') {
                        navigate('/verwaltung/mitarbeiter', { 
                            state: { 
                                from: '/tools/leader', 
                                openWorkerId: id,
                                openAbsenceId: options.absenceId,
                                targetTab: options.action === 'absences' ? 'calendar' : 'grid',
                                conflictState,
                                fromConflictCenter: true
                            } 
                        });
                    } else if (type === 'vehicle') {
                        navigate('/verwaltung/fuhrpark', { state: { fromLeader: true, conflictState, fromConflictCenter: true } });
                    } else if (type === 'kolonne') {
                        openKolonnenManagement(id);
                    }
                    setConflictOverviewOpen(false);
                }}
                onConflictActiveTabChange={setConflictActiveTab}
                onConflictExpandedResourcesChange={setConflictExpandedResources}
                onConflictCollapsedSectionsChange={setConflictCollapsedSections}
                conflictActiveTab={conflictActiveTab}
                conflictExpandedResources={conflictExpandedResources}
                conflictCollapsedSections={conflictCollapsedSections}
                onCloseKolonnenManagement={() => {
                    setKolonnenManagementOpen(false);
                    if (navigationSource === 'conflict-overview') {
                        setConflictOverviewOpen(true);
                    }
                }}
                onCloseAbsenceModal={() => {
                    setAbsenceModalOpen(false);
                    setAbsenceWorkerId(null);
                    if (navigationSource === 'conflict-overview') {
                        setConflictOverviewOpen(true);
                    }
                    setNavigationSource(null);
                }}
            />


            {contextMenu && (
                <div 
                    className="context-menu-backdrop"
                    onClick={(e) => {
                        e.stopPropagation();
                        setContextMenu(null);
                    }}
                    onContextMenu={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setContextMenu(null);
                    }}
                />
            )}
            {contextMenu && (
                <div
                    className={`calendar-context-menu ${contextMenu.openLeft ? 'open-left' : ''}`}
                    style={{ left: contextMenu.x, top: contextMenu.y }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {contextMenu.assignment ? (
                        <>
                            <div className="context-menu-item" onClick={() => handleMenuAction('edit', contextMenu)}>✏️ Bearbeiten</div>
                            <div className="context-menu-item has-submenu">
                                <span>🔄 Status setzen</span>
                                <span className="submenu-arrow">▶</span>
                                <div className="context-submenu">
                                    {STATUS_OPTIONS.map(opt => (
                                        <div 
                                            key={opt.value} 
                                            className="context-menu-item"
                                            onClick={() => handleMenuAction('status', { ...contextMenu, status: opt.value })}
                                        >
                                            <span className="status-dot-mini" style={{ background: opt.color }} />
                                            {opt.label}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="context-menu-item" onClick={() => handleMenuAction('copy', contextMenu)}>📋 Kopieren</div>
                            {contextMenu.assignment.start_datetime && (
                                <div className="context-menu-item" onClick={() => handleMenuAction('undated', contextMenu)}>📝 In undatiert verschieben</div>
                            )}
                            <div className="context-menu-divider" />
                            <div className="context-menu-item delete" onClick={() => handleMenuAction('delete', contextMenu)}>🗑️ Löschen</div>
                        </>
                    ) : (
                        <>
                            <div className={`context-menu-item ${!clipboard ? 'disabled' : ''}`} onClick={() => clipboard && handleMenuAction('paste', contextMenu)}>
                                📥 Einfügen {clipboard ? `(${clipboard.project_name})` : ''}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
